/**
 * CheckinScheduleModal Component
 *
 * Modal para exibir a agenda de check-ins planejados/agendados.
 * Mostra um calendário simples com as visitas programadas.
 *
 * @since 2025-12-19
 * @module tmf_checkin
 */

import { useState, useCallback, useMemo } from 'react';
import { Modal, Spinner } from '@/components/ui';
import { useCheckinList } from '@/hooks/useCheckin';
import { useAuth } from '@/contexts/AuthContext';
import type { Checkin } from '@/types/checkin';
import clsx from 'clsx';

interface CheckinScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Dias da semana em portugues
const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

// Meses em portugues
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

/**
 * Gera os dias do calendario para um mes especifico
 */
function getCalendarDays(year: number, month: number): (number | null)[] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startWeekday = firstDay.getDay();

  const days: (number | null)[] = [];

  // Dias vazios antes do primeiro dia do mes
  for (let i = 0; i < startWeekday; i++) {
    days.push(null);
  }

  // Dias do mes
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  return days;
}

/**
 * Formata data para exibicao
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formata hora para exibicao
 */
function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CheckinScheduleModal({ isOpen, onClose }: CheckinScheduleModalProps) {
  const { user } = useAuth();
  const today = new Date();

  // Estado do calendario
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Datas para buscar check-ins do mes atual
  const dateFrom = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    return firstDay.toISOString().split('T')[0];
  }, [currentYear, currentMonth]);

  const dateTo = useMemo(() => {
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    return lastDay.toISOString().split('T')[0];
  }, [currentYear, currentMonth]);

  // Buscar check-ins do mes
  const { data: checkins, loading, error } = useCheckinList({
    person_id: user?.id,
    date_from: dateFrom,
    date_to: dateTo,
    page_size: 100,
  });

  // Agrupar check-ins por dia
  const checkinsByDay = useMemo(() => {
    const map = new Map<number, Checkin[]>();
    checkins.forEach((checkin) => {
      const date = new Date(checkin.datetime_checkin);
      if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
        const day = date.getDate();
        if (!map.has(day)) {
          map.set(day, []);
        }
        map.get(day)!.push(checkin);
      }
    });
    return map;
  }, [checkins, currentMonth, currentYear]);

  // Check-ins do dia selecionado
  const selectedDayCheckins = useMemo(() => {
    if (!selectedDate) return [];
    const day = selectedDate.getDate();
    return checkinsByDay.get(day) || [];
  }, [selectedDate, checkinsByDay]);

  // Navegacao do calendario
  const goToPreviousMonth = useCallback(() => {
    if (currentMonth === 0) {
      setCurrentYear((y) => y - 1);
      setCurrentMonth(11);
    } else {
      setCurrentMonth((m) => m - 1);
    }
    setSelectedDate(null);
  }, [currentMonth]);

  const goToNextMonth = useCallback(() => {
    if (currentMonth === 11) {
      setCurrentYear((y) => y + 1);
      setCurrentMonth(0);
    } else {
      setCurrentMonth((m) => m + 1);
    }
    setSelectedDate(null);
  }, [currentMonth]);

  const goToToday = useCallback(() => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
    setSelectedDate(today);
  }, [today]);

  // Dias do calendario
  const calendarDays = useMemo(
    () => getCalendarDays(currentYear, currentMonth),
    [currentYear, currentMonth]
  );

  // Handler para selecionar um dia
  const handleDayClick = useCallback(
    (day: number) => {
      setSelectedDate(new Date(currentYear, currentMonth, day));
    },
    [currentYear, currentMonth]
  );

  // Verificar se um dia e hoje
  const isToday = useCallback(
    (day: number) => {
      return (
        day === today.getDate() &&
        currentMonth === today.getMonth() &&
        currentYear === today.getFullYear()
      );
    },
    [today, currentMonth, currentYear]
  );

  // Verificar se um dia esta selecionado
  const isSelected = useCallback(
    (day: number) => {
      if (!selectedDate) return false;
      return (
        day === selectedDate.getDate() &&
        currentMonth === selectedDate.getMonth() &&
        currentYear === selectedDate.getFullYear()
      );
    },
    [selectedDate, currentMonth, currentYear]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Agenda de Visitas" size="lg">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Calendario */}
        <div>
          {/* Header do calendario */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <i className="bi bi-chevron-left text-muted-foreground" />
            </button>

            <div className="text-center">
              <h4 className="font-semibold text-foreground">
                {MONTHS[currentMonth]} {currentYear}
              </h4>
              <button
                type="button"
                onClick={goToToday}
                className="text-xs text-orange-600 hover:text-orange-700"
              >
                Hoje
              </button>
            </div>

            <button
              type="button"
              onClick={goToNextMonth}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <i className="bi bi-chevron-right text-muted-foreground" />
            </button>
          </div>

          {/* Dias da semana */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEKDAYS.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-medium text-muted-foreground py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Dias do mes */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => {
              if (day === null) {
                return <div key={`empty-${index}`} className="p-2" />;
              }

              const hasCheckins = checkinsByDay.has(day);
              const checkinCount = checkinsByDay.get(day)?.length || 0;

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDayClick(day)}
                  className={clsx(
                    'relative p-2 text-sm rounded-lg transition-colors',
                    isSelected(day)
                      ? 'bg-orange-500 text-white'
                      : isToday(day)
                      ? 'bg-orange-100 text-orange-700 font-semibold'
                      : hasCheckins
                      ? 'bg-green-50 text-foreground hover:bg-green-100'
                      : 'text-muted-foreground hover:bg-muted'
                  )}
                >
                  {day}
                  {hasCheckins && (
                    <span
                      className={clsx(
                        'absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full',
                        isSelected(day) ? 'bg-card' : 'bg-green-500'
                      )}
                      title={`${checkinCount} check-in(s)`}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legenda */}
          <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full" />
              <span>Com visitas</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 bg-orange-500 rounded-full" />
              <span>Selecionado</span>
            </div>
          </div>
        </div>

        {/* Lista de check-ins do dia selecionado */}
        <div className="border-t md:border-t-0 md:border-l border-border md:pl-4 pt-4 md:pt-0">
          <h4 className="font-semibold text-foreground mb-3">
            {selectedDate
              ? `Visitas em ${formatDate(selectedDate.toISOString())}`
              : 'Selecione um dia'}
          </h4>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="sm" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <i className="bi bi-exclamation-circle text-2xl mb-2" />
              <p className="text-sm">Erro ao carregar agenda</p>
            </div>
          ) : selectedDate && selectedDayCheckins.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {selectedDayCheckins.map((checkin) => (
                <div
                  key={checkin.id}
                  className="p-3 bg-muted rounded-lg border border-border"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                        checkin.datetime_checkout ? 'bg-green-500' : 'bg-orange-500'
                      )}
                    >
                      <i className="bi bi-geo-alt-fill text-white text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h5 className="font-medium text-foreground text-sm truncate">
                        {checkin.resource?.resource_name || 'Local'}
                      </h5>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <span>
                          <i className="bi bi-clock mr-1" />
                          {formatTime(checkin.datetime_checkin)}
                        </span>
                        {checkin.datetime_checkout && (
                          <>
                            <span>-</span>
                            <span>{formatTime(checkin.datetime_checkout)}</span>
                          </>
                        )}
                      </div>
                      {!checkin.datetime_checkout && (
                        <span className="inline-block mt-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-medium rounded">
                          Em andamento
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : selectedDate ? (
            <div className="text-center py-8 text-muted-foreground">
              <i className="bi bi-calendar-x text-3xl mb-2" />
              <p className="text-sm">Nenhuma visita neste dia</p>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <i className="bi bi-calendar3 text-3xl mb-2" />
              <p className="text-sm">Clique em um dia para ver as visitas</p>
            </div>
          )}

          {/* Resumo do mes */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <h5 className="font-medium text-blue-900 text-sm mb-2">
              Resumo de {MONTHS[currentMonth]}
            </h5>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <i className="bi bi-calendar-check text-blue-600" />
                <span className="text-muted-foreground">
                  {checkins.length} visita(s)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <i className="bi bi-geo-alt text-blue-600" />
                <span className="text-muted-foreground">
                  {checkinsByDay.size} dia(s)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default CheckinScheduleModal;
